import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { BookOpen, Layers, Sparkles, Wrench } from 'lucide-react';

import {
  SUBJECT_CATALOG,
  getSubjectLucideIcon,
} from '../constants/subjectCatalog';

export interface StudioIconRailProps {
  onBuild: () => void;
}

const railLinkClass = ({ isActive }: { isActive: boolean }) =>
  ['studio-icon-rail__btn', isActive ? 'studio-icon-rail__btn--active' : ''].filter(Boolean).join(' ');

const RAIL_SUBJECTS = SUBJECT_CATALOG.filter((s) => s.id !== 'general');

/**
 * Rail Studio: Cursos · Crear · Taller interactivo + asignaturas (biblioteca /studio/asignatura/:id).
 */
export function StudioIconRail({ onBuild }: StudioIconRailProps) {
  const { pathname } = useLocation();
  const onCoursesHub =
    pathname === '/studio' || pathname.startsWith('/studio/curso/');
  const onSubjectHub = pathname.startsWith('/studio/asignatura/');
  const onTools = pathname.startsWith('/studio/herramientas');
  const activeSubjectId = onSubjectHub
    ? pathname.split('/')[3]?.split('?')[0] || null
    : null;

  return (
    <aside className="studio-icon-rail" aria-label="Navegación Studio">
      <NavLink to="/studio" className="studio-icon-rail__brand" title="Edusico Studio" end={false}>
        <span className="studio-icon-rail__mark" aria-hidden>
          <Layers className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <span className="sr-only">Studio</span>
      </NavLink>

      <nav className="studio-icon-rail__nav" aria-label="Principal">
        <NavLink
          to="/studio"
          end
          className={() =>
            [
              'studio-icon-rail__btn',
              onCoursesHub && !onTools ? 'studio-icon-rail__btn--active' : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          title="Mis cursos"
        >
          <BookOpen className="h-5 w-5" strokeWidth={2} aria-hidden />
          <span className="studio-icon-rail__label">Cursos</span>
        </NavLink>

        <button
          type="button"
          className="studio-icon-rail__btn studio-icon-rail__btn--action"
          title="Construir sin curso (material, planificación o rúbrica)"
          onClick={onBuild}
        >
          <Sparkles className="h-5 w-5" strokeWidth={2.25} aria-hidden />
          <span className="studio-icon-rail__label">Crear</span>
        </button>

        <NavLink
          to="/studio/herramientas"
          className={railLinkClass}
          title="Taller interactivo — motores y recetas de sala"
        >
          <Wrench className="h-5 w-5" strokeWidth={2} aria-hidden />
          <span className="studio-icon-rail__label studio-icon-rail__label--wrap">
            Taller interactivo
          </span>
        </NavLink>
      </nav>

      <div className="studio-icon-rail__divider" aria-hidden />

      <nav className="studio-icon-rail__subjects" aria-label="Asignaturas">
        {RAIL_SUBJECTS.map((subject) => {
          const active = activeSubjectId === subject.id;
          const SubjectIcon = getSubjectLucideIcon(subject.id);
          return (
            <NavLink
              key={subject.id}
              to={`/studio/asignatura/${subject.id}`}
              className={() =>
                [
                  'studio-icon-rail__btn',
                  'studio-icon-rail__btn--subject',
                  active ? 'studio-icon-rail__btn--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
              title={`${subject.name} · biblioteca`}
              style={{ ['--subject-hue' as string]: subject.hue }}
            >
              <SubjectIcon className="h-5 w-5" strokeWidth={2} aria-hidden />
              <span className="studio-icon-rail__label studio-icon-rail__label--subject">
                {shortSubjectLabel(subject.name)}
              </span>
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

function shortSubjectLabel(name: string): string {
  const map: Record<string, string> = {
    Matemáticas: 'Mate',
    'Lenguaje y Comunicación': 'Lengua',
    Música: 'Música',
    'Ciencias Naturales': 'Ciencia',
    'Historia y Geografía': 'Hist.',
    Historia: 'Hist.',
    Inglés: 'Inglés',
    'Educación Física y Salud': 'Ed. Fís.',
  };
  return map[name] ?? name.slice(0, 6);
}
